import React, { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'
import type { ActivePunchGroup, MapperLocation, SearchTarget } from './types'
import { locationDisplayAddress } from '../../../services/locationAddress'

type DraftShape =
  | { type: 'circle'; circle: { lat: number; lon: number; radius: number }; polygon: null }
  | { type: 'polygon'; circle: null; polygon: Array<[number, number]> }

type Props = {
  locations: MapperLocation[]
  selectedId: string
  draft: MapperLocation
  activePunches: Record<string, ActivePunchGroup>
  searchTarget: SearchTarget | null
  canEdit: boolean
  canDelete: boolean
  canCreateJobs?: boolean
  onSelectLocation: (id: string) => void
  onCreateJobFromLocation?: (location: MapperLocation) => void
  onShapeDraft: (shape: DraftShape) => void
  onArchiveLocation: (id: string) => void
}

const defaultCenter: [number, number] = [45.5017, -73.5673]
const OSM_MAX_ZOOM = 19

function locationKey(location: MapperLocation) {
  return location.locationKey || location.key || location.id || ''
}

function activeCountFor(location: MapperLocation, activePunches: Record<string, ActivePunchGroup>) {
  const key = locationKey(location)
  return key ? activePunches[key]?.people?.length || 0 : 0
}

function escapeHtml(value: unknown) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function shapeFromLayer(layer: L.Layer): DraftShape | null {
  if (layer instanceof L.Circle) {
    const center = layer.getLatLng()
    return {
      type: 'circle',
      circle: { lat: Number(center.lat.toFixed(6)), lon: Number(center.lng.toFixed(6)), radius: Math.round(layer.getRadius()) },
      polygon: null,
    }
  }

  if (layer instanceof L.Polygon) {
    const raw = layer.getLatLngs()[0]
    const points = Array.isArray(raw)
      ? raw
          .map((point) => point as L.LatLng)
          .map((point) => [Number(point.lat.toFixed(6)), Number(point.lng.toFixed(6))] as [number, number])
      : []
    if (points.length >= 3) {
      return { type: 'polygon', circle: null, polygon: points }
    }
  }

  return null
}

function layerForLocation(location: MapperLocation, selected: boolean, activeCount: number) {
  const stroke = selected ? '#4338ca' : activeCount > 0 ? '#16a34a' : '#4f46e5'
  const fill = selected ? '#c7d2fe' : activeCount > 0 ? '#bbf7d0' : '#dbeafe'
  const options = {
    color: stroke,
    fillColor: fill,
    weight: selected ? 4 : 2,
    fillOpacity: selected ? 0.28 : 0.18,
  }

  if (location.type === 'circle' && location.circle) {
    return L.circle([location.circle.lat, location.circle.lon], {
      ...options,
      radius: location.circle.radius,
    })
  }

  if (location.type === 'polygon' && location.polygon?.length) {
    return L.polygon(location.polygon, options)
  }

  return null
}

function draftLayerFor(location: MapperLocation) {
  if (location.type === 'circle' && location.circle) {
    if (!Number.isFinite(location.circle.lat) || !Number.isFinite(location.circle.lon) || !Number.isFinite(location.circle.radius) || location.circle.radius <= 0) {
      return null
    }
    return L.circle([location.circle.lat, location.circle.lon], {
      radius: location.circle.radius,
      color: '#0f172a',
      fillColor: '#facc15',
      fillOpacity: 0.18,
      weight: 3,
      dashArray: '7 6',
    })
  }
  if (location.type === 'polygon' && location.polygon?.length) {
    return L.polygon(location.polygon, {
      color: '#0f172a',
      fillColor: '#facc15',
      fillOpacity: 0.18,
      weight: 3,
      dashArray: '7 6',
    })
  }
  return null
}

function popupHtml(location: MapperLocation, activeCount: number, canEdit: boolean, canDelete: boolean, canCreateJobs: boolean) {
  const addressText = locationDisplayAddress(location)
  const shiftText = location.requireShift ? 'Requires scheduled shift' : 'Allows unscheduled punches'
  const trackingText = location.activeTracking || location.trackingEnabled ? 'Tracking enabled' : 'Tracking off'
  const companyName = location.ownerOrgName || location.owner_org_name || location.company || 'Company unavailable'
  const editButton = canEdit && location.id ? `<button type="button" class="mapper-popup__button" data-map-action="edit" data-id="${location.id}">Edit</button>` : ''
  const createJobButton = canCreateJobs && location.id ? `<button type="button" class="mapper-popup__button" data-map-action="create-job" data-id="${location.id}" data-owner-org-id="${escapeHtml(location.ownerOrgId || location.owner_org_id || location.companyOrgId || location.company_org_id || '')}">Create job here</button>` : ''
  const deleteButton = canDelete && location.id ? `<button type="button" class="mapper-popup__button mapper-popup__button--danger" data-map-action="archive" data-id="${location.id}">Archive</button>` : ''
  return `
    <div class="mapper-popup">
      <strong>${escapeHtml(location.name || 'Untitled location')}</strong>
      <span>${escapeHtml(companyName)}</span>
      ${addressText ? `<span>${escapeHtml(addressText)}</span>` : ''}
      <span>${location.type === 'polygon' ? 'Polygon fence' : 'Circle fence'}</span>
      <span>${activeCount} active punch${activeCount === 1 ? '' : 'es'}</span>
      <span>${shiftText}</span>
      <span>${trackingText}</span>
      <div class="mapper-popup__actions">${editButton}${createJobButton}${deleteButton}</div>
    </div>
  `
}

export const MapperLeafletMap: React.FC<Props> = ({
  locations,
  selectedId,
  draft,
  activePunches,
  searchTarget,
  canEdit,
  canDelete,
  onCreateJobFromLocation,
  canCreateJobs = Boolean(onCreateJobFromLocation),
  onSelectLocation,
  onShapeDraft,
  onArchiveLocation,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const drawControlRef = useRef<L.Control.Draw | null>(null)
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null)
  const existingFencesRef = useRef<L.FeatureGroup | null>(null)
  const punchLayerRef = useRef<L.LayerGroup | null>(null)
  const searchLayerRef = useRef<L.LayerGroup | null>(null)
  const hasFitFencesRef = useRef(false)
  const callbacksRef = useRef({ onSelectLocation, onCreateJobFromLocation, onShapeDraft, onArchiveLocation, locations })

  useEffect(() => {
    callbacksRef.current = { onSelectLocation, onCreateJobFromLocation, onShapeDraft, onArchiveLocation, locations }
  }, [locations, onSelectLocation, onCreateJobFromLocation, onShapeDraft, onArchiveLocation])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, { zoomControl: true, maxZoom: OSM_MAX_ZOOM }).setView(defaultCenter, 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: OSM_MAX_ZOOM,
      maxNativeZoom: OSM_MAX_ZOOM,
    }).addTo(map)

    existingFencesRef.current = L.featureGroup().addTo(map)
    drawnItemsRef.current = L.featureGroup().addTo(map)
    punchLayerRef.current = L.layerGroup().addTo(map)
    searchLayerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map

    const onCreated = (event: L.LeafletEvent) => {
      const layer = (event as L.DrawEvents.Created).layer
      drawnItemsRef.current?.clearLayers()
      drawnItemsRef.current?.addLayer(layer)
      const shape = shapeFromLayer(layer)
      if (shape) callbacksRef.current.onShapeDraft(shape)
    }

    const onEdited = (event: L.LeafletEvent) => {
      ;(event as L.DrawEvents.Edited).layers.eachLayer((layer) => {
        const shape = shapeFromLayer(layer)
        if (shape) callbacksRef.current.onShapeDraft(shape)
      })
    }

    const onDeleted = () => {
      drawnItemsRef.current?.clearLayers()
    }

    const onMapClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const actionButton = target?.closest<HTMLElement>('[data-map-action]')
      if (!actionButton) return
      const id = actionButton.dataset.id || ''
      if (!id) return
      if (actionButton.dataset.mapAction === 'archive') {
        callbacksRef.current.onArchiveLocation(id)
      } else if (actionButton.dataset.mapAction === 'create-job') {
        const location = callbacksRef.current.locations.find((item) => item.id === id)
        if (location && callbacksRef.current.onCreateJobFromLocation) {
          callbacksRef.current.onCreateJobFromLocation(location)
        } else {
          callbacksRef.current.onSelectLocation(id)
        }
      } else {
        callbacksRef.current.onSelectLocation(id)
      }
    }

    map.on(L.Draw.Event.CREATED, onCreated)
    map.on(L.Draw.Event.EDITED, onEdited)
    map.on(L.Draw.Event.DELETED, onDeleted)
    map.getContainer().addEventListener('click', onMapClick)

    return () => {
      map.getContainer().removeEventListener('click', onMapClick)
      map.off(L.Draw.Event.CREATED, onCreated)
      map.off(L.Draw.Event.EDITED, onEdited)
      map.off(L.Draw.Event.DELETED, onDeleted)
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const drawnItems = drawnItemsRef.current
    if (!map || !drawnItems) return

    if (drawControlRef.current) {
      map.removeControl(drawControlRef.current)
      drawControlRef.current = null
    }
    if (!canEdit) return

    drawControlRef.current = new L.Control.Draw({
      draw: {
        marker: false,
        polyline: false,
        rectangle: false,
        circlemarker: false,
        circle: {
          shapeOptions: { color: '#4f46e5', fillColor: '#c7d2fe', fillOpacity: 0.22 },
        },
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: '#4f46e5', fillColor: '#c7d2fe', fillOpacity: 0.22 },
        },
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    })
    map.addControl(drawControlRef.current)
  }, [canEdit])

  const draftKey = useMemo(() => JSON.stringify({
    selectedId,
    type: draft.type,
    circle: draft.circle,
    polygon: draft.polygon,
  }), [selectedId, draft.type, draft.circle, draft.polygon])

  useEffect(() => {
    const drawnItems = drawnItemsRef.current
    if (!drawnItems) return
    drawnItems.clearLayers()
    const layer = draftLayerFor(draft)
    if (layer) drawnItems.addLayer(layer)
  }, [draftKey])

  useEffect(() => {
    const group = existingFencesRef.current
    if (!group) return
    group.clearLayers()

    locations.forEach((location) => {
      const id = location.id || ''
      const count = activeCountFor(location, activePunches)
      const layer = layerForLocation(location, id === selectedId, count)
      if (!layer) return
      layer.on('click', () => id && onSelectLocation(id))
      layer.bindPopup(popupHtml(location, count, canEdit, canDelete, canCreateJobs), { maxWidth: 260 })
      group.addLayer(layer)
    })

    const map = mapRef.current
    if (map && group.getLayers().length && !hasFitFencesRef.current) {
      map.fitBounds(group.getBounds().pad(0.15))
      hasFitFencesRef.current = true
    }
  }, [locations, selectedId, activePunches, canEdit, canDelete, canCreateJobs, onSelectLocation])

  useEffect(() => {
    const layerGroup = punchLayerRef.current
    if (!layerGroup) return
    layerGroup.clearLayers()
    Object.values(activePunches).forEach((group) => {
      ;(group.people || []).forEach((person) => {
        if (person.lat === null || person.lat === undefined || person.lon === null || person.lon === undefined) return
        const marker = L.circleMarker([person.lat, person.lon], {
          radius: 7,
          color: '#15803d',
          fillColor: '#22c55e',
          fillOpacity: 0.9,
          weight: 2,
        })
        marker.bindTooltip(escapeHtml(`${person.workerName || person.phone || 'Worker'} ${person.action || ''}`.trim()), { direction: 'top' })
        marker.bindPopup(`
          <div class="mapper-popup">
            <strong>${escapeHtml(person.workerName || 'Unknown worker')}</strong>
            <span>${escapeHtml(person.phone || '')}</span>
            <span>${escapeHtml(person.action || 'Active')}</span>
            <span>${escapeHtml(group.label || group.name || group.key)}</span>
          </div>
        `)
        layerGroup.addLayer(marker)
      })
    })
  }, [activePunches])

  useEffect(() => {
    const map = mapRef.current
    const layerGroup = searchLayerRef.current
    if (!map || !layerGroup || !searchTarget) return
    layerGroup.clearLayers()
    const marker = L.circleMarker([searchTarget.lat, searchTarget.lon], {
      radius: 8,
      color: '#be123c',
      fillColor: '#fb7185',
      fillOpacity: 0.85,
      weight: 2,
    }).bindPopup(escapeHtml(searchTarget.label))
    layerGroup.addLayer(marker)
    map.setView([searchTarget.lat, searchTarget.lon], 16)
    marker.openPopup()
  }, [searchTarget])

  return <div ref={mapContainerRef} className="mapper-map" aria-label="Interactive geofence map" />
}
